/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unused-vars */
import { useMemo, useState, useEffect, useCallback } from 'react';
import { Modal, Upload, message, Button, Row, Col, Select, Spin } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import {
  UploadedContactSourceData,
  UploadedContactSourceResponse,
  ContactSourceMapItem,
} from 'lib.store/src/type-organizer/audiences/models.ts';
import {
  useLazyGetContactSourcesQuery,
  usePostContactSourceImportMutation,
} from 'lib.store/src/type-organizer/audiences/audiences.api.ts';
import { DeskIcon } from 'lib.icons/src';
import { useAuth } from 'lib.context/src/AuthContext';
import { showApiError } from 'lib.shared/src/handleApiError';
import { apiBaseUrl } from 'lib.shared/src/api';
import './styles.scss';

const { Dragger } = Upload;
const { Option } = Select;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (source: { id: string; file_name: string; map: ContactSourceMapItem[] }) => void;
}

interface UploadErrorResponse {
  errors?: Record<string, string[]>;
  message?: string;
}

const normalizeKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase());

const normalizeSourceMap = (
  map: Array<{
    code?: string;
    name?: string;
    field?: string;
  }> = [],
): ContactSourceMapItem[] =>
  map.map((item) => ({
    code: item.code ?? '',
    name: item.name ?? '',
    field: item.field ?? '',
  }));

const MAX_FILE_SIZE_MB = 50;

const ContactUploadModal = ({ isOpen, onClose, onSuccess }: Props) => {
  const [uploadedFileData, setUploadedFileData] = useState<UploadedContactSourceData | null>(null);
  const [headers, setHeaders] = useState<string[]>([]); // file column names
  const [selectedFields, setSelectedFields] = useState<string[]>([]); // backend field keys
  const { user } = useAuth();
  const organizationId = user?.organization?.id?.toString();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSourceId, setProcessingSourceId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(
    null,
  );

  const [postImport, { isLoading: isImportLoading }] = usePostContactSourceImportMutation();
  const [getContactSources] = useLazyGetContactSourcesQuery();

  const progressPercent = useMemo(
    () =>
      importProgress?.total ? Math.round((importProgress.current / importProgress.total) * 100) : 0,
    [importProgress],
  );

  const handleClose = useCallback(() => {
    setUploadedFileData(null);
    setHeaders([]);
    setSelectedFields([]);
    setIsProcessing(false);
    setProcessingSourceId(null);
    setIsUploading(false);
    setImportProgress(null);
    onClose();
  }, [onClose]);

  const uploadProps = {
    name: 'file',
    multiple: false,
    action: `${apiBaseUrl}/organizer/contact-sources/import/upload`,
    accept: '.csv',
    beforeUpload: (file: File) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        void message.error(`Files larger than ${MAX_FILE_SIZE_MB} MB are not allowed.`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: (info: UploadChangeParam<UploadFile>) => {
      const { status } = info.file;
      if (status === 'uploading') {
        setIsUploading(true);
      } else if (status === 'done') {
        setIsUploading(false);
        const response = info.file.response as UploadedContactSourceResponse;
        const normalizedMap = normalizeSourceMap(response.data.map);
        const normalizedData: UploadedContactSourceData = {
          ...response.data,
          map: normalizedMap,
        };
        const allHeaders = normalizedData.headers
          ? Object.keys(normalizedData.headers)
          : normalizedData.list.length
            ? Object.keys(normalizedData.list[0])
            : [];
        const selected = allHeaders.map((headerKey) => {
          const headerLabel = normalizedData.headers?.[headerKey] ?? headerKey;
          const mapItem = normalizedData.map.find(
            (m) => m.code === headerKey || m.name === headerLabel,
          );
          return mapItem?.field ?? '';
        });
        setUploadedFileData(normalizedData);
        setHeaders(allHeaders);
        setSelectedFields(selected);
      } else if (status === 'error') {
        setIsUploading(false);
        const response = info.file.response as UploadErrorResponse | undefined;
        const errorObj = {
          status: 422,
          data: { errors: response?.errors, message: response?.message },
        };
        showApiError(errorObj, `${info.file.name} file upload failed.`, true);
      }
    },
    showUploadList: false,
  };

  const handleFieldChange = (index: number, value: string) => {
    const newFields = [...selectedFields];
    const oldField = newFields[index];
    if (value && value !== 'for_segment_use') {
      const existingIndex = newFields.findIndex((h) => h === value);
      if (existingIndex !== -1 && existingIndex !== index) {
        newFields[existingIndex] = oldField;
      }
    }
    newFields[index] = value;
    setSelectedFields(newFields);
  };

  const handleApprove = async () => {
    if (!uploadedFileData) return;
    const newMapArr = selectedFields.reduce<ContactSourceMapItem[]>((acc, field, idx) => {
      if (field) {
        const code = headers[idx];
        const name = uploadedFileData.headers?.[code] ?? code;
        acc.push({ code, field, name });
      }
      return acc;
    }, []);
    try {
      const response = (await postImport({
        contactSource: uploadedFileData.source_id,
        map: newMapArr,
      }).unwrap()) as any;
      const source = response?.source ?? response?.data ?? response;
      const sourceId = source?.id?.toString() ?? uploadedFileData.source_id;

      if (source?.is_imported) {
        void message.success('Contacts imported successfully');
        onSuccess({
          id: sourceId,
          file_name: source.file_name,
          map: normalizeSourceMap(source.map),
        });
        handleClose();
        return;
      }

      setProcessingSourceId(sourceId);
      setIsProcessing(true);
      setImportProgress(null);
    } catch (e: unknown) {
      showApiError(e, 'Error importing contacts', true);
    }
  };

  const headerOptions = uploadedFileData
    ? uploadedFileData.fields
        .filter((value) => value && value !== 'for_segment_use')
        .map((key) => ({
          label: normalizeKey(key),
          value: key,
        }))
    : [];

  const columnWidths = useMemo(() => {
    if (!headers.length) return [];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return headers.map(() => 100);
    ctx.font =
      '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

    const maxOptionWidth = Math.max(
      ...headerOptions.filter((o) => o.label).map((option) => ctx.measureText(option.label).width),
      0,
    );

    return headers.map((header) => {
      const headerLabel = uploadedFileData?.headers?.[header] ?? normalizeKey(header);
      const headerWidth = ctx.measureText(headerLabel).width;
      return Math.max(headerWidth, maxOptionWidth) + 32; // padding
    });
  }, [headers, headerOptions, uploadedFileData]);

  const tableWidth = useMemo(() => columnWidths.reduce((sum, w) => sum + w, 0), [columnWidths]);

  useEffect(() => {
    if (!processingSourceId || !organizationId) return;
    const channel = window.Echo?.private(`boost.organization.${organizationId}`);
    const importedHandler = (response: {
      source: { id: number; file_name: string; map: ContactSourceMapItem[] };
    }) => {
      if (response?.source?.id?.toString() === processingSourceId) {
        void message.success('Contacts imported successfully');
        onSuccess({
          id: response.source.id.toString(),
          file_name: response.source.file_name,
          map: normalizeSourceMap(response.source.map as ContactSourceMapItem[]),
        });
        handleClose();
      }
    };
    const importingHandler = (response: {
      source: { id: number };
      lines: { total: number; current: number };
    }) => {
      if (response?.source?.id?.toString() === processingSourceId) {
        setImportProgress((prev) => {
          if (!prev || response.lines.current >= prev.current) {
            return {
              current: response.lines.current,
              total: response.lines.total,
            };
          }
          return prev;
        });
      }
    };
    channel?.listen('.organizer.contacts.imported', importedHandler);
    channel?.listen('.organizer.contacts.importing', importingHandler);

    // pages without an initialized websocket connection (e.g. onboarding)
    // fall back to polling the imported-sources list instead
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    if (!channel) {
      pollTimer = setInterval(() => {
        getContactSources({ page: 1 })
          .unwrap()
          .then((response) => {
            const source = response.data.find((item) => item.id === processingSourceId);
            if (source) {
              void message.success('Contacts imported successfully');
              onSuccess({
                id: source.id,
                file_name: source.file_name,
                map: normalizeSourceMap(source.map ?? []),
              });
              handleClose();
            }
          })
          .catch(() => {});
      }, 3000);
    }

    return () => {
      channel?.stopListening('.organizer.contacts.imported', importedHandler);
      channel?.stopListening('.organizer.contacts.importing', importingHandler);
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [processingSourceId, organizationId, handleClose, onSuccess, getContactSources]);

  return (
    <Modal
      title="Import contacts from CSV"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width="80%"
      destroyOnClose
      closable={!isProcessing}
      maskClosable={!isProcessing}
    >
      <div className="contact-upload">
        <Spin spinning={isUploading}>
          {!uploadedFileData ? (
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon contact-upload__drop-icon">
                <DeskIcon name="list" size={32} />
              </p>
              <div className="contact-upload__drop-title">Your list of contacts</div>
              <div className="contact-upload__drop-hint">
                A .csv file up to {MAX_FILE_SIZE_MB} MB. Drop it here or choose it.
              </div>
              <Button>Choose a file</Button>
            </Dragger>
          ) : isProcessing ? (
            <div className="contact-upload__processing">
              <Spin />
              <span className="contact-upload__processing-text">
                {importProgress ? `${progressPercent}%` : 'Importing contacts…'}
              </span>
            </div>
          ) : (
            <>
              <div className="contact-upload__lead">
                Match the fields from the file to the table columns
              </div>
              <div
                className="custom-invitations-table"
                style={{ overflowX: 'auto', width: '100%' }}
              >
                <Row className="table-header" wrap={false} style={{ minWidth: `${tableWidth}px` }}>
                  {headers.map((header, index) => (
                    <Col key={index} flex={`${columnWidths[index]}px`}>
                      <div className="table-header__cell">
                        <span className="table-header__cell-title">
                          {uploadedFileData?.headers?.[header] ?? normalizeKey(header)}
                        </span>
                        <Select
                          value={selectedFields[index]}
                          onChange={(value) => handleFieldChange(index, value)}
                          style={{ width: '100%' }}
                          dropdownMatchSelectWidth={false}
                          dropdownStyle={{ minWidth: 220 }}
                        >
                          <Option key="empty" value="">
                            —
                          </Option>
                          <Option key="for_segment_use" value="for_segment_use">
                            <span className="for-segment-use-option">
                              Make segments from values
                            </span>
                          </Option>
                          {headerOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  ))}
                </Row>
                {uploadedFileData.list.map((person, index) => (
                  <Row
                    key={index}
                    className="table-row"
                    wrap={false}
                    style={{ minWidth: `${tableWidth}px` }}
                  >
                    {headers.map((header, colIndex) => (
                      <Col key={colIndex} flex={`${columnWidths[colIndex]}px`}>
                        <span className="invitations-csv-modal-cell">{person[header]}</span>
                      </Col>
                    ))}
                  </Row>
                ))}
              </div>
              <div className="contact-upload__footer">
                <Button onClick={handleClose} disabled={isProcessing}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={() => void handleApprove()}
                  loading={isImportLoading || isProcessing}
                  disabled={isProcessing}
                >
                  Import contacts
                </Button>
              </div>
            </>
          )}
        </Spin>
      </div>
    </Modal>
  );
};

export default ContactUploadModal;
